import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCompetition } from '../../contexts/CompetitionContext';
import { useCompetitionSummary } from '../../hooks/useCompetitionSummary';
import {
  DASHBOARD_RECENT_RESULTS_ID,
  DASHBOARD_UPCOMING_MATCHES_ID
} from '../../constants/dashboardAnchors';
import { CompetitionSelector } from '../competition/CompetitionSelector';
import {
  Menu,
  X,
  LogOut,
  Settings,
  LayoutDashboard,
  Trophy,
  ChevronDown,
  Shield,
  Calendar,
  Flag,
  HelpCircle
} from 'lucide-react';

/**
 * Sticky top navigation: brand, competition, primary links, user summary + menu.
 */
export function TopNav() {
  const { user, logout } = useAuth();
  const { t, language, isRTL, toggleLanguage } = useLanguage();
  const location = useLocation();
  const {
    competitions,
    competitionSlug,
    setCompetition,
    basePath,
    loading: competitionsLoading
  } = useCompetition();
  const { points, rank, loading: summaryLoading } = useCompetitionSummary();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const userMenuRef = useRef(null);
  /** Swallow one ghost click on the backdrop right after open (touch → synthetic click). */
  const swallowBackdropClickRef = useRef(false);

  const isAuthPage =
    location.pathname === '/login' || location.pathname === '/register';

  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setUserOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileOpen]);

  const closeMobileMenu = useCallback(() => setMobileOpen(false), []);

  const openMobileMenu = useCallback(() => {
    setUserOpen(false);
    swallowBackdropClickRef.current = true;
    setMobileOpen(true);
  }, []);

  useLayoutEffect(() => {
    if (!mobileOpen) return;
    const onClickCapture = (e) => {
      const drawer = document.getElementById('mobile-nav-drawer');
      const backdrop = document.getElementById('mobile-nav-backdrop');
      if (!drawer?.contains(e.target)) {
        return;
      }
      if (e.target !== backdrop && !backdrop?.contains(e.target)) {
        swallowBackdropClickRef.current = false;
        return;
      }
      if (!swallowBackdropClickRef.current) return;
      swallowBackdropClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }
    };
    document.addEventListener('click', onClickCapture, true);
    const t = window.setTimeout(() => {
      swallowBackdropClickRef.current = false;
    }, 800);
    return () => {
      document.removeEventListener('click', onClickCapture, true);
      window.clearTimeout(t);
    };
  }, [mobileOpen]);

  const dashboardPath = `${basePath}/dashboard`;
  const dashboardUpcomingTo = `${dashboardPath}#${DASHBOARD_UPCOMING_MATCHES_ID}`;
  const dashboardRecentResultsTo = `${dashboardPath}#${DASHBOARD_RECENT_RESULTS_ID}`;

  const scrollToUpcomingMatches = useCallback(() => {
    document.getElementById(DASHBOARD_UPCOMING_MATCHES_ID)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, []);

  const scrollToRecentResults = useCallback(() => {
    document.getElementById(DASHBOARD_RECENT_RESULTS_ID)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, []);

  if (!user || isAuthPage) return null;

  const displayName = user.fullName || user.username || 'User';
  const initials = displayName.charAt(0).toUpperCase();

  const navLink = (path, label, icon, onNavigate) => {
    const active = location.pathname === path || location.pathname.startsWith(path + '/');
    return (
      <Link
        to={path}
        className={[
          'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          active
            ? 'bg-brand-100 text-brand-800'
            : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
        ].join(' ')}
        onClick={onNavigate}
      >
        {icon}
        {label}
      </Link>
    );
  };

  const recentResultsLabel =
    language === 'en' ? 'Recent results' : 'תוצאות אחרונות';

  const recentResultsLinkClass =
    'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900';

  const dashboardNavActive =
    location.pathname === dashboardPath ||
    location.pathname.startsWith(`${dashboardPath}/`);
  const dashboardPrimaryClass = [
    'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
    dashboardNavActive
      ? 'bg-brand-100 text-brand-800'
      : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
  ].join(' ');

  const upcomingMatchesLabel =
    language === 'en' ? 'Upcoming matches' : 'משחקים קרובים';

  return (
    <>
    <header
      className="sticky top-0 isolate z-[100] border-b border-zinc-200/80 bg-white shadow-sm"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="mx-auto flex h-14 w-full max-w-7xl items-center gap-2 px-3 sm:h-16 sm:gap-4 sm:px-4 lg:px-6">
        {/* Logo — start of row; hamburger lives at end (next to avatar) on mobile */}
        <Link
          to={`${basePath}/dashboard`}
          className="flex min-w-0 shrink-0 items-center gap-2 font-bold tracking-tight text-zinc-900"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-brand-600 to-brand-900 text-sm font-black text-white shadow-md">
            FB
          </span>
          <span className="hidden text-base sm:inline">Football Bets</span>
        </Link>

        {/* Competition — desktop center */}
        <div className="hidden min-w-0 flex-1 justify-center px-2 md:flex">
          <CompetitionSelector
            competitions={competitions}
            value={competitionSlug}
            onChange={setCompetition}
            disabled={competitionsLoading}
            className="max-w-xs"
          />
        </div>

        {/* Desktop nav (lg+); hamburger "Open menu" is lg:hidden below */}
        <nav className="hidden items-center gap-1 lg:flex">
          <Link
            to={dashboardUpcomingTo}
            className={dashboardPrimaryClass}
            onClick={(e) => {
              if (location.pathname === dashboardPath) {
                e.preventDefault();
                scrollToUpcomingMatches();
                window.history.replaceState(null, '', dashboardUpcomingTo);
              }
            }}
          >
            <LayoutDashboard className="h-4 w-4" />
            {upcomingMatchesLabel}
          </Link>
          <Link
            to={dashboardRecentResultsTo}
            className={recentResultsLinkClass}
            onClick={(e) => {
              if (location.pathname === dashboardPath) {
                e.preventDefault();
                scrollToRecentResults();
                window.history.replaceState(
                  null,
                  '',
                  dashboardRecentResultsTo
                );
              }
            }}
          >
            <Flag className="h-4 w-4" />
            {recentResultsLabel}
          </Link>
          {navLink(
            `${basePath}/predictions`,
            language === 'en' ? 'Predictions' : 'תחזיות',
            <HelpCircle className="h-4 w-4" />
          )}
          {navLink(
            `${basePath}/leaderboard`,
            t('leaderboard'),
            <Trophy className="h-4 w-4" />
          )}
          {user.isAdmin && (
            <Link
              to="/admin/dashboard"
              className={[
                'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                location.pathname.startsWith('/admin')
                  ? 'bg-brand-100 text-brand-800'
                  : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
              ].join(' ')}
            >
              <Shield className="h-4 w-4" />
              {language === 'en' ? 'Admin' : 'ניהול'}
            </Link>
          )}
        </nav>

        {/* User + mobile menu — ms-auto pins group to trailing edge (right in LTR, left in RTL) */}
        <div className="ms-auto flex shrink-0 items-center gap-2 sm:gap-3">
          <div
            className="hidden rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-right sm:block"
            title={language === 'en' ? 'Your points this competition' : 'הנקודות שלך'}
          >
            <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
              {language === 'en' ? 'Points' : 'נקודות'}
            </p>
            <p className="text-lg font-bold leading-tight tabular-nums text-zinc-900">
              {summaryLoading ? '…' : points}
              {!summaryLoading && rank != null && (
                <span className="ml-2 text-xs font-normal text-zinc-500">#{rank}</span>
              )}
            </p>
          </div>

          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setUserOpen(!userOpen)}
              className="flex items-center gap-2 rounded-xl border border-zinc-200 bg-white py-1.5 pl-2 pr-2 shadow-sm transition hover:border-zinc-300 sm:pl-2 sm:pr-3"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-sm font-bold text-white">
                {initials}
              </span>
              <span className="hidden max-w-[120px] truncate text-left text-sm font-semibold text-zinc-800 sm:block">
                {displayName}
              </span>
              <ChevronDown className="hidden h-4 w-4 text-zinc-400 sm:block" />
            </button>

            {userOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 w-56 rounded-xl border border-zinc-200 bg-white py-2 shadow-lg ring-1 ring-black/5">
                <div className="border-b border-zinc-100 px-3 pb-2 sm:hidden">
                  <p className="text-xs text-zinc-500">{language === 'en' ? 'Points' : 'נקודות'}</p>
                  <p className="text-xl font-bold tabular-nums">
                    {summaryLoading ? '…' : points}
                  </p>
                </div>
                <Link
                  to="/settings"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                  onClick={() => setUserOpen(false)}
                >
                  <Settings className="h-4 w-4" />
                  {language === 'en' ? 'Settings' : 'הגדרות'}
                </Link>
                {user.isAdmin && (
                  <Link
                    to="/admin/dashboard"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                    onClick={() => setUserOpen(false)}
                  >
                    <Shield className="h-4 w-4" />
                    {t('adminDashboard')}
                  </Link>
                )}
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                  onClick={toggleLanguage}
                >
                  <span className="text-xs font-semibold uppercase">
                    {language === 'en' ? 'עב' : 'EN'}
                  </span>
                  {language === 'en' ? 'עברית' : 'English'}
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  onClick={() => {
                    setUserOpen(false);
                    logout();
                  }}
                >
                  <LogOut className="h-4 w-4" />
                  {t('logout')}
                </button>
              </div>
            )}
          </div>

          {/* Main nav drawer trigger — mobile / tablet only; hidden from lg (desktop) */}
          <div className="shrink-0 lg:hidden">
            <button
              type="button"
              className="relative z-[120] flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-800 shadow-sm touch-manipulation [-webkit-tap-highlight-color:transparent]"
              aria-label={language === 'en' ? 'Open menu' : 'פתח תפריט'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav-drawer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openMobileMenu();
              }}
            >
              <Menu className="pointer-events-none h-5 w-5 shrink-0" aria-hidden />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile competition row */}
      <div className="border-t border-zinc-100 px-3 py-2 md:hidden">
        <CompetitionSelector
          competitions={competitions}
          value={competitionSlug}
          onChange={setCompetition}
          disabled={competitionsLoading}
          className="w-full"
        />
      </div>
    </header>

    {mobileOpen &&
      typeof document !== 'undefined' &&
      createPortal(
        <div
          id="mobile-nav-drawer"
          className="fixed inset-0 z-[30000] lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label={language === 'en' ? 'Main menu' : 'תפריט ראשי'}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <button
            id="mobile-nav-backdrop"
            type="button"
            tabIndex={-1}
            className="absolute inset-0 bg-zinc-900/50"
            aria-label={language === 'en' ? 'Close menu' : 'סגור תפריט'}
            onClick={closeMobileMenu}
          />
          <div
            className={`absolute top-0 z-[1] flex h-full w-[min(100%,320px)] flex-col bg-white shadow-2xl ring-1 ring-black/10 ${
              isRTL ? 'left-0' : 'right-0'
            }`}
          >
            <div className="flex items-center justify-between border-b border-zinc-100 p-4">
              <span className="font-bold text-zinc-900">
                {language === 'en' ? 'Menu' : 'תפריט'}
              </span>
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-zinc-200 [-webkit-tap-highlight-color:transparent]"
                onClick={closeMobileMenu}
                aria-label={language === 'en' ? 'Close' : 'סגור'}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-col gap-1 overflow-y-auto p-3">
              <Link
                to={dashboardUpcomingTo}
                className={dashboardPrimaryClass}
                onClick={(e) => {
                  if (location.pathname === dashboardPath) {
                    e.preventDefault();
                    scrollToUpcomingMatches();
                    window.history.replaceState(null, '', dashboardUpcomingTo);
                  }
                  closeMobileMenu();
                }}
              >
                <LayoutDashboard className="h-4 w-4" />
                {upcomingMatchesLabel}
              </Link>
              <Link
                to={dashboardRecentResultsTo}
                className={recentResultsLinkClass}
                onClick={(e) => {
                  if (location.pathname === dashboardPath) {
                    e.preventDefault();
                    scrollToRecentResults();
                    window.history.replaceState(
                      null,
                      '',
                      dashboardRecentResultsTo
                    );
                  }
                  closeMobileMenu();
                }}
              >
                <Flag className="h-4 w-4" />
                {recentResultsLabel}
              </Link>
              {navLink(
                `${basePath}/predictions`,
                language === 'en' ? 'Predictions' : 'תחזיות',
                <HelpCircle className="h-4 w-4" />,
                closeMobileMenu
              )}
              {navLink(
                `${basePath}/leaderboard`,
                t('leaderboard'),
                <Trophy className="h-4 w-4" />,
                closeMobileMenu
              )}
              {navLink(
                `${basePath}/upcoming-games`,
                language === 'en' ? 'Upcoming' : 'עתידיים',
                <Calendar className="h-4 w-4" />,
                closeMobileMenu
              )}
              {navLink(
                `${basePath}/completed-games`,
                language === 'en' ? 'Results' : 'תוצאות',
                <Flag className="h-4 w-4" />,
                closeMobileMenu
              )}
              {user.isAdmin && (
                <Link
                  to="/admin/dashboard"
                  className={[
                    'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    location.pathname.startsWith('/admin')
                      ? 'bg-brand-100 text-brand-800'
                      : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
                  ].join(' ')}
                  onClick={closeMobileMenu}
                >
                  <Shield className="h-4 w-4" />
                  {language === 'en' ? 'Admin' : 'ניהול'}
                </Link>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
